import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
} from "@/components/ui/card";
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "@/components/ui/input-otp";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

// Local defaults
const RESEND_COOLDOWN_SUCCESS = 30; // seconds
const RESEND_COOLDOWN_RATE_LIMIT = 300; // seconds
const OTP_DEFAULT_EXPIRY = 300; // seconds

function formatTime(seconds: number) {
    if (typeof seconds !== "number" || seconds < 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
}

interface OtpCardProps {
    email?: string;
    otp: string;
    setOtp: (otp: string) => void;
    onVerify: (otp: string) => void;
    onBack: () => void;
    error: any;
    loading: boolean;
    onResend?: () => Promise<any>;
    expiresIn?: number;
}

export function OtpCard({
    email = "user@example.com",
    otp,
    setOtp,
    onVerify,
    onBack,
    error,
    loading,
    onResend,
    expiresIn,
}: Readonly<OtpCardProps>) {
    const [expiryTime, setExpiryTime] = useState(expiresIn || OTP_DEFAULT_EXPIRY);
    const [resendCooldown, setResendCooldown] = useState(30);
    const [resendStatus, setResendStatus] = useState<{
        loading: boolean;
        error: string | null;
    }>({
        loading: false,
        error: null,
    });
    const [localError, setLocalError] = useState<string | null>(null);
    const [localSuccess, setLocalSuccess] = useState<string | null>(null);

    const derivedState = useMemo(() => {
        const attemptsRemaining =
            typeof error === "object" && error?.attemptsRemaining !== undefined
                ? error.attemptsRemaining
                : null;

        const isOtpExpired = expiryTime === 0 || error?.type === "EXPIRED";
        const isMaxAttemptsReached =
            attemptsRemaining === 0 || resendStatus.error === "MAX_ATTEMPTS_EXCEEDED";
        const isRateLimited = resendStatus.error === "OTP_RESEND_RATE_LIMITED";

        let alertMessage = localError || (typeof error === "string" ? error : null);
        if (isOtpExpired) {
            alertMessage = "Code has expired. Please request a new one.";
        } else if (isMaxAttemptsReached) {
            alertMessage = "Maximum attempts reached. Please request a new OTP.";
        } else if (isRateLimited) {
            alertMessage = `Too many OTP requests. Please wait ${formatTime(
                resendCooldown
            )} before trying again.`;
        }

        const isInputDisabled =
            isOtpExpired || loading || isMaxAttemptsReached || isRateLimited;
        const isVerifyDisabled =
            isInputDisabled || otp.length !== 6 || resendStatus.loading;

        return {
            alertMessage,
            isInputDisabled,
            isVerifyDisabled,
            isOtpExpired,
            isMaxAttemptsReached,
            isRateLimited,
        };
    }, [error, expiryTime, resendStatus, loading, otp, resendCooldown, localError]);

    useEffect(() => {
        if (expiresIn) {
            setExpiryTime(expiresIn);
        }
    }, [expiresIn]);

    useEffect(() => {
        if (expiryTime > 0) {
            const timer = setInterval(
                () => setExpiryTime((prev) => Math.max(0, prev - 1)),
                1000
            );
            return () => clearInterval(timer);
        }
    }, [expiryTime]);

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setInterval(
                () => setResendCooldown((prev) => Math.max(0, prev - 1)),
                1000
            );
            return () => clearInterval(timer);
        }
    }, [resendCooldown]);

    useEffect(() => {
        if (localSuccess) {
            const timer = setTimeout(() => setLocalSuccess(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [localSuccess]);

    const maskEmail = (emailStr: string) => {
        if (!emailStr) return "";
        const [local, domain] = emailStr.split("@");
        if (!local || !domain) return emailStr;
        const prefix = local.substring(0, 3);
        return `${prefix}****@${domain}`;
    };

    const handleResend = async () => {
        if (resendCooldown > 0 || resendStatus.loading) return;

        setResendStatus({ loading: true, error: null });
        setLocalError(null);
        setLocalSuccess(null);
        setOtp("");

        try {
            const result = await onResend?.();

            if (result?.success) {
                setLocalSuccess(result?.message || "OTP sent successfully!");
                setResendCooldown(RESEND_COOLDOWN_SUCCESS);
                setExpiryTime(result.expiresIn || OTP_DEFAULT_EXPIRY);
                setResendStatus({ loading: false, error: null });

                setTimeout(() => {
                    // Focus input if needed, though InputOTP handles focus well
                }, 200);
            } else {
                const resendError = result?.error;
                setResendStatus({ loading: false, error: resendError });

                if (resendError === "OTP_RESEND_RATE_LIMITED") {
                    setResendCooldown(RESEND_COOLDOWN_RATE_LIMIT);
                } else if (resendError === "MAX_ATTEMPTS_EXCEEDED") {
                    setLocalError(result?.message || "Too many attempts. Please request a new OTP.");
                } else {
                    setLocalError(result?.message || "Failed to resend OTP. Please try again.");
                }
            }
        } catch (err) {
            setResendStatus({ loading: false, error: "GENERIC_ERROR" });
            setLocalError((err as Error).message || "An error occurred. Please try again.");
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (derivedState.isVerifyDisabled) {
            if (otp.trim().length !== 6) {
                setLocalError("Please enter the 6-digit verification code.");
            }
            return;
        }
        onVerify?.(otp);
    };

    const handleBack = () => {
        setOtp("");
        onBack();
    };

    return (
        <Card className="w-full max-w-md mx-auto shadow-lg border-slate-100">
            <CardContent className="pt-5 pb-5 px-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Title */}
                    <h2 className="text-xl font-semibold text-center text-slate-900">
                        One Time Passcode
                    </h2>

                    {/* Explanation Text */}
                    <p className="text-sm text-slate-600 text-center leading-relaxed">
                        We've sent a verification code to your email address. To sign in to your account, 
                        check your email <span className="font-medium text-slate-900">({maskEmail(email)})</span> and 
                        input the verification code below.
                    </p>

                    {/* OTP Input Fields */}
                    <div className="flex justify-center py-2">
                        <InputOTP
                            maxLength={6}
                            value={otp}
                            onChange={setOtp}
                            disabled={derivedState.isInputDisabled}
                        >
                            <InputOTPGroup>
                                <InputOTPSlot index={0} className="h-12 w-12 text-base border-2" />
                                <InputOTPSlot index={1} className="h-12 w-12 text-base border-2" />
                                <InputOTPSlot index={2} className="h-12 w-12 text-base border-2" />
                                <InputOTPSlot index={3} className="h-12 w-12 text-base border-2" />
                                <InputOTPSlot index={4} className="h-12 w-12 text-base border-2" />
                                <InputOTPSlot index={5} className="h-12 w-12 text-base border-2" />
                            </InputOTPGroup>
                        </InputOTP>
                    </div>

                    {/* Error Messages */}
                    {derivedState.alertMessage && (
                        <Alert variant="destructive" className="py-2">
                            <AlertDescription className="text-sm">{derivedState.alertMessage}</AlertDescription>
                        </Alert>
                    )}

                    {localSuccess && (
                        <Alert className="border-green-500 text-green-600 py-2">
                            <AlertDescription className="text-sm">{localSuccess}</AlertDescription>
                        </Alert>
                    )}

                    {/* Expiry and Resend Code Link */}
                    <div className="flex justify-between items-center text-sm text-slate-600 pt-1">
                        <span>
                            Code expires in <span className="text-slate-900 font-medium">{formatTime(expiryTime)}</span>
                        </span>
                        <button
                            onClick={handleResend}
                            disabled={resendCooldown > 0 || resendStatus.loading || loading}
                            type="button"
                            className="text-blue-600 hover:underline disabled:text-slate-400 disabled:cursor-not-allowed font-normal"
                            style={{ all: 'unset', cursor: (resendCooldown > 0 || resendStatus.loading || loading) ? 'not-allowed' : 'pointer', color: (resendCooldown > 0 || resendStatus.loading || loading) ? '#94a3b8' : '#2563eb' }}
                        >
                            {(() => {
                                if (resendStatus.loading) return "Resending...";
                                if (resendCooldown > 0)
                                    return `Resend (${resendCooldown}s)`;
                                return "Resend Code";
                            })()}
                        </button>
                    </div>

                    {/* Verify Button */}
                    <Button
                        type="submit"
                        className="w-full h-10 text-base font-medium bg-slate-200 hover:bg-slate-300 text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={derivedState.isVerifyDisabled}
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            "Verify"
                        )}
                    </Button>

                    {/* Back Link */}
                    <div className="text-center pt-1">
                        <button
                            onClick={handleBack}
                            type="button"
                            disabled={loading}
                            className="text-sm text-blue-600 hover:underline disabled:text-slate-400 disabled:cursor-not-allowed font-normal"
                            style={{ all: 'unset', cursor: loading ? 'not-allowed' : 'pointer', color: loading ? '#94a3b8' : '#2563eb', fontSize: '0.875rem' }}
                        >
                            &lt; Back
                        </button>
                    </div>
                </form>
            </CardContent>
        </Card>
    );
}
